import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  TableSortLabel,
  Pagination,
  Fab,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { strategyService, Strategy, StrategyExecution } from '../../services/strategyService';
import StrategyEditor from './StrategyEditor';

// Sorting utilities
function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const Strategies: React.FC = () => {
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState<Strategy | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  // Modal-related states removed - now using dedicated page

  // Sorting states
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof StrategyExecution>('started_at');

  useEffect(() => {
    loadStrategies();
  }, []);

  const loadStrategies = async () => {
    try {
      setLoading(true);
      const data = await strategyService.getStrategies();
      setStrategies(data);
    } catch (error: any) {
      console.error('Error loading strategies:', error);
      toast.error(error.response?.data?.message || 'Failed to load strategies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStrategy = () => {
    setSelectedStrategy(null);
    setIsEditing(false);
    setDialogOpen(true);
  };

  const handleEditStrategy = (strategy: Strategy) => {
    setSelectedStrategy(strategy);
    setIsEditing(true);
    setDialogOpen(true);
  };

  const handleDeleteStrategy = async (strategyId: string) => {
    if (!window.confirm('Are you sure you want to delete this strategy?')) {
      return;
    }

    try {
      await strategyService.deleteStrategy(strategyId);
      toast.success('Strategy deleted successfully');
      loadStrategies();
    } catch (error: any) {
      console.error('Error deleting strategy:', error);
      toast.error(error.response?.data?.message || 'Failed to delete strategy');
    }
  };

  const handleRunStrategy = async (strategyId: string) => {
    try {
      await strategyService.runStrategy(strategyId);
      toast.success('Strategy execution started');
      // Reload strategies to get updated execution status
      setTimeout(() => loadStrategies(), 1000);
    } catch (error: any) {
      console.error('Error running strategy:', error);
      toast.error(error.response?.data?.message || 'Failed to run strategy');
    }
  };

  const handleToggleActive = async (strategy: Strategy) => {
    try {
      await strategyService.updateStrategy(strategy.id, {
        ...strategy,
        is_active: !strategy.is_active
      });
      toast.success(`Strategy ${strategy.is_active ? 'deactivated' : 'activated'}`);
      loadStrategies();
    } catch (error: any) {
      console.error('Error updating strategy:', error);
      toast.error(error.response?.data?.message || 'Failed to update strategy');
    }
  };

  const handleViewResults = (execution: StrategyExecution) => {
    navigate(`/strategy-results/${execution.id}`);
  };

  const handleRequestSort = (property: keyof StrategyExecution) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const formatDuration = (seconds: number | null | undefined) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box px={1} py={0.5}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6" component="h1">
          Trading Strategies
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateStrategy}
        >
          Create Strategy
        </Button>
      </Box>

      {strategies.length === 0 ? (
        <Alert severity="info">
          No strategies found. Create your first strategy to get started.
        </Alert>
      ) : (
        <Grid container spacing={1.5}>
          {strategies.map((strategy) => (
            <Grid item xs={12} md={6} lg={6} key={strategy.id}>
              <Card sx={{ height: 'fit-content', maxHeight: 500, display: 'flex', flexDirection: 'column' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" component="h2">
                      {strategy.name}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={strategy.is_active}
                            onChange={() => handleToggleActive(strategy)}
                            size="small"
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                      <Tooltip title="Edit Strategy">
                        <IconButton size="small" onClick={() => handleEditStrategy(strategy)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Run Strategy">
                        <IconButton size="small" onClick={() => handleRunStrategy(strategy.id)}>
                          <PlayArrowIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Strategy">
                        <IconButton size="small" onClick={() => handleDeleteStrategy(strategy.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Typography variant="body2" color="textSecondary" mb={1}>
                    {strategy.description || 'No description provided'}
                  </Typography>

                  <Box display="flex" gap={0.5} mb={1} flexWrap="wrap">
                    <Chip
                      label={strategy.is_active ? 'Active' : 'Inactive'}
                      color={strategy.is_active ? 'success' : 'default'}
                      size="small"
                    />
                    <Chip
                      label={strategy.schedule_type || 'Manual'}
                      icon={<ScheduleIcon />}
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  {/* Recent Executions */}
                  {strategy.executions && strategy.executions.length > 0 && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Recent Executions
                      </Typography>
                      <TableContainer sx={{ maxHeight: 250, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Status</TableCell>
                              <TableCell>Started</TableCell>
                              <TableCell align="right">Duration</TableCell>
                              <TableCell align="right">Results</TableCell>
                              <TableCell align="right">Changes</TableCell>
                              <TableCell align="center">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {strategy.executions.map((execution) => (
                              <TableRow key={execution.id} hover>
                                <TableCell>
                                  <Chip
                                    label={execution.status}
                                    color={execution.status === 'completed' ? 'success' : 
                                           execution.status === 'failed' ? 'error' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {execution.started_at ? new Date(execution.started_at).toLocaleDateString() : 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2">
                                    {formatDuration(execution.duration_seconds)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2">
                                    {execution.result_count || 0}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2">
                                    {execution.changes_detected || 0}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  {(execution.result_count || 0) > 0 && (
                                    <Tooltip title="View Results">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleViewResults(execution)}
                                      >
                                        <VisibilityIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <StrategyEditor
        open={dialogOpen}
        strategy={selectedStrategy}
        onClose={() => setDialogOpen(false)}
        onSave={() => {
          setDialogOpen(false);
          loadStrategies();
          toast.success('Strategy saved successfully');
        }}
      />

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="create strategy"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1000,
        }}
        onClick={handleCreateStrategy}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default Strategies;